import { Injectable } from '@angular/core';

import { CustomMessage } from '@models/CustomMessage';
import { LogLevel } from '@models/LogLevel';

import { AppEnvironment } from '../../environments/AppEnvironment';
import { environment } from '../../environments/environment';

@Injectable({
	providedIn: 'root',
})

/**
 * Service for logging messages with various log levels.
 */
export class LoggingService {
	private currentEnvironment: AppEnvironment = AppEnvironment.DEV;
	private externalLoggingEnabled: boolean = false;
	private isProduction: boolean = false;
	private readonly internalLogger: Console = console;

	constructor() {
		this.setEnvironment(environment.appEnvironment as AppEnvironment);
	}

	/**
	 * Logs a LOG message to the console window or adds it to session storage.
	 *
	 * @param message - The console message to be logged.
	 * @param enableExternal - Indicates whether to enable external logging (e.g., session storage).
	 * @returns - void (Logs to console and/or session storage)
	 */
	public log<T>(
		message: CustomMessage<T>,
		enableExternal: boolean = false
	): void {
		const stringiedMessage = this.messageConverter(message);
		this.logger(stringiedMessage, LogLevel.LOG, enableExternal);
	}

	/**
	 * Logs a DEBUG message to the console window or adds it to session storage.
	 *
	 * @param message - The debug message to be logged.
	 * @param enableExternal - Indicates whether to enable external logging (e.g., session storage).
	 * @returns - void (Logs to console and/or session storage)
	 */
	public debug<T>(
		message: CustomMessage<T>,
		enableExternal: boolean = false
	): void {
		const stringiedMessage = this.messageConverter(message);
		this.logger(stringiedMessage, LogLevel.DEBUG, enableExternal);
	}

	/**
	 * Logs an INFO message to the console window or adds it to session storage.
	 *
	 * @param message - The info message to be logged.
	 * @param enableExternal - Indicates whether to enable external logging (e.g., session storage).
	 * @returns - void (Logs to console and/or session storage)
	 */
	public info<T>(
		message: CustomMessage<T>,
		enableExternal: boolean = false
	): void {
		const stringiedMessage = this.messageConverter(message);
		this.logger(stringiedMessage, LogLevel.INFO, enableExternal);
	}

	/**
	 * Logs a WARNING message to the console window adds it to session storage.
	 *
	 * @param message - The warning message to be logged.
	 * @param enableExternal - Indicates whether to enable external logging (e.g., session storage).
	 * @returns - void (Logs to console and/or session storage)
	 */
	public warn<T>(
		message: CustomMessage<T>,
		enableExternal: boolean = false
	): void {
		const stringiedMessage = this.messageConverter(message);
		this.logger(stringiedMessage, LogLevel.WARNING, enableExternal);
	}

	/**
	 * Logs an ERROR message to the console window or adds it to session storage.
	 *
	 * @param message - The error message to be logged.
	 * @param stack - The stack trace for error messages (optional).
	 * @param enableExternal - Indicates whether to enable external logging (e.g., session storage).
	 * @returns - void (Logs to console and/or session storage)
	 */
	public error<T>(
		message: CustomMessage<T>,
		stack: string = '',
		enableExternal: boolean = true
	): void {
		const stringiedMessage = this.messageConverter(message);
		this.logger(stringiedMessage, LogLevel.ERROR, enableExternal, stack);
	}

	// ****************************PRIVATE METHODS****************************

	/**
	 * Middleware style method that handles the log message and decides what to do based on log level and current environment.
	 *
	 * @param message - The message to be logged.
	 * @param logLevel- The log level (default is LogLevel.Info).
	 * @param enableExternal - Indicates whether to enable external logging (e.g., session storage).
	 * @param stack - The stack trace for error messages (optional).
	 * @returns - void
	 */
	private logger(
		message: string,
		logLevel: LogLevel,
		enableExternal: boolean,
		stack?: string
	): void {
		const formattedMessage = this.formatMessage(message, logLevel);
		this.enableExternalLogging(enableExternal);

		if (logLevel === LogLevel.ERROR) {
			// TODO: Add logic to send error to Sentry or other error tracking service
			const shouldLogToSessionStorage =
				this.externalLoggingEnabled && !this.isProduction;

			this.outputLog(formattedMessage, logLevel, stack);

			if (shouldLogToSessionStorage) {
				this.storeLogsToSession(formattedMessage, logLevel, stack);
				this.enableExternalLogging(false);
			}
		}

		this.outputLog(formattedMessage, logLevel);
	}

	/**
	 * Outputs a log message using either the console or stores it in session storage based on the log level and environment.
	 *
	 * @param message - The message to be logged.
	 * @param logLevel - The log level (default is LogLevel.Info).
	 * @param stack - The stack trace for error messages (optional).
	 * @returns - void
	 */
	private outputLog(message: string, logLevel: LogLevel, stack?: string): void {
		if (!this.isProduction) {
			this.logHandler(message, logLevel, stack);

			if (this.externalLoggingEnabled) {
				this.storeLogsToSession(message, logLevel, stack);
			}
		}

		if (logLevel === LogLevel.ERROR && this.isProduction) {
			this.storeLogsToSession(message, logLevel, stack);
		}
	}

	/**
	 * Sets the logger based on the log level and logs the message using the appropriate console method.
	 *
	 * @param message - The message to be logged.
	 * @param logLevel - The log level to determine the appropriate console method.
	 * @param stack - The stack trace for error messages (optional).
	 * @returns - void
	 */
	private logHandler(
		message: string,
		logLevel: LogLevel,
		stack?: string
	): void {
		switch (logLevel) {
			case LogLevel.DEBUG:
				this.internalLogger.debug(message);
				break;
			case LogLevel.INFO:
				this.internalLogger.info(message);
				break;
			case LogLevel.WARNING:
				this.internalLogger.warn(message);
				break;
			case LogLevel.ERROR:
				this.internalLogger.error(message, stack);
				break;
			case LogLevel.LOG:
				this.internalLogger.log(message);
				break;
			default:
				break;
		}
	}

	/**
	 * Stores logs in session storage, including the provided log message, log level, and stack trace.
	 *
	 * @param message - The log message to be stored.
	 * @param logLevel - The log level as a string.
	 * @param stack - The stack trace for error messages (optional).
	 */
	private storeLogsToSession(
		message: string,
		logLevel: LogLevel,
		stack: string | undefined
	): void {
		const existingLogs = sessionStorage.getItem('sessionLogs') || '[]';
		const logs = JSON.parse(existingLogs);
		logs.push(
			`[EXTERNAL LOGGING - ${this.currentEnvironment.toUpperCase()}] [${logLevel.toUpperCase()}] ${message} : ${stack}`
		);
		sessionStorage.setItem('sessionLogs', JSON.stringify(logs));
	}

	/**
	 * Formats a log message by adding a timestamp and log level.
	 *
	 * @param message - The message to be formatted.
	 * @param logLevel - The log level.
	 * @returns - The formatted log message.
	 */
	private formatMessage(message: string, logLevel: LogLevel): string {
		const timestamp = new Date();

		const formattedTimestamp = `${this.padNumber(
			timestamp.getMonth() + 1
		)}/${this.padNumber(
			timestamp.getDate()
		)}/${timestamp.getFullYear()} - ${this.padNumber(
			timestamp.getHours()
		)}:${this.padNumber(timestamp.getMinutes())}:${this.padNumber(
			timestamp.getSeconds()
		)}`;

		return `[${formattedTimestamp}] [${logLevel.toUpperCase()}] ${message}`;
	}

	/**
	 * Pads a number with a leading zero if it is less than 10.
	 *
	 * @param num - The number to be padded.
	 * @returns - The padded number.
	 */
	private padNumber(num: number): string {
		return num < 10 ? `0${num}` : `${num}`; // eslint-disable-line @typescript-eslint/no-magic-numbers
	}

	/**
	 * Enables or disables external logging.
	 *
	 * @param enable - Indicates whether to enable external logging.
	 */
	private enableExternalLogging(enable: boolean): void {
		this.externalLoggingEnabled = enable;
	}

	/**
	 * Sets the environment based on whether the application is in production.
	 *
	 * @param env - Indicates whether the application is in production.
	 */
	private setEnvironment(env: AppEnvironment): void {
		this.isProduction = env === AppEnvironment.PROD;
		this.currentEnvironment = env;
	}

	/**
	 * Converts a message to a string.
	 *
	 * @param message - The message to be converted into a string.
	 * @returns - The converted message.
	 */
	private messageConverter<T>(message: CustomMessage<T>): string {
		if (typeof message !== 'string') {
			return JSON.stringify(message) as string;
		}

		return message;
	}
}
